/**
 * Az új verzió észlelésének tiszta szabályai és konstansai.
 * Spec: docs/superpowers/specs/2026-09-16-app-update-detection-design.md (7.2–7.3)
 */

/** A build-stamp szkript által írt fájl, a document.baseURI-hoz képest. */
export const VERSION_FILE = 'version.json';

/** Rendszeres ellenőrzés, amíg a lap látható. */
export const CHECK_INTERVAL_MS = 5 * 60 * 1000;

/** Előtérbe kerüléskor ennél sűrűbben nem ellenőriz. */
export const MIN_CHECK_GAP_MS = 30 * 1000;

/** Az induló ellenőrzés csak ennyi időn belül tölthet újra azonnal (utána már dolgozhat a user). */
export const BOOT_RELOAD_WINDOW_MS = 10 * 1000;

/** Ugyanarra a célra ennyi időn belül nincs második automatikus újratöltés (hurokvédelem). */
export const RELOAD_GUARD_WINDOW_MS = 5 * 60 * 1000;

/** sessionStorage kulcs az utolsó automatikus újratöltési kísérlethez. */
export const RELOAD_ATTEMPT_KEY = 'appUpdate.reloadAttempt';

/** Minden célra érvényes kísérlet: ha a sessionStorage nem elérhető, nincs automatikus újratöltés. */
export const ANY_RELOAD_TARGET = '*';

const CHUNK_LOAD_ERROR =
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Loading chunk [\w-]+ failed|ChunkLoadError/i;

export interface ReloadAttempt {
  readonly target: string;
  readonly at: number;
}

export function parseVersionFile(body: unknown): string | null {
  const buildId = typeof body === 'object' && body !== null ? (body as { buildId?: unknown }).buildId : undefined;
  return typeof buildId === 'string' && buildId.trim() !== '' ? buildId.trim() : null;
}

/** Nincs „újabb/régebbi” összevetés: egy visszaállított régebbi build is átállást vált ki. */
export const isDifferentBuild = (currentId: string, remoteId: string | null): remoteId is string => remoteId !== null && remoteId !== currentId;

export const urlPath = (url: string): string => url.split(/[?#]/, 1)[0];

/** Csak az útvonal változása számít oldalváltásnak (a szűrők és keresők query-váltása nem). */
export const isPathChange = (fromUrl: string, toUrl: string): boolean => urlPath(fromUrl) !== urlPath(toUrl);

function errorText(error: unknown): string {
  if (error instanceof Error) {
    return `${error.name} ${error.message}`;
  }
  return typeof error === 'string' ? error : '';
}

export const isChunkLoadError = (error: unknown): boolean => CHUNK_LOAD_ERROR.test(errorText(error));

export function canAutoReload(attempt: ReloadAttempt | null, target: string, now: number): boolean {
  if (!attempt || (attempt.target !== target && attempt.target !== ANY_RELOAD_TARGET)) {
    return true;
  }
  return now - attempt.at >= RELOAD_GUARD_WINDOW_MS;
}

export const shouldReloadOnBoot = (elapsedMs: number): boolean => elapsedMs <= BOOT_RELOAD_WINDOW_MS;

export const buildReloadTarget = (buildId: string): string => `build:${buildId}`;

export const chunkReloadTarget = (url: string): string => `chunk:${urlPath(url)}`;

export function parseReloadAttempt(raw: string | null): ReloadAttempt | null {
  if (!raw) {
    return null;
  }
  try {
    const value = JSON.parse(raw) as Partial<ReloadAttempt> | null;
    return typeof value?.target === 'string' && typeof value.at === 'number' ? { target: value.target, at: value.at } : null;
  } catch {
    return null;
  }
}
